import {
  Box,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  FormattedRelativeTime,
  Icon,
  PageLoader,
  PlainButton,
  Stack,
  Text,
  Truncated,
} from '@customerio/pluma-components/react';
import { PageTitle } from '@/components/page-title';
import { useWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { generatePath } from '@/services/router/generate-path';
import { AgentLayout } from '../layout';
import { useSkills } from './hooks/use-skills';
import type { Skill } from './hooks/use-skills';
import * as styles from './index.css';

function SkillCard({ skill, workspaceId }: { skill: Skill; workspaceId: number }) {
  const href = generatePath('/workspaces/:workspaceId/agent/skills/:skillPath', {
    workspaceId,
    skillPath: encodeURIComponent(skill.fileName),
  });

  return (
    <PlainButton href={href} className={styles.skillCard}>
      <Box display="flex" alignItems="center" gap="100" minW="0">
        <Icon name="star" size="xs" color="subtle" />
        <Text size="s" weight="semibold">
          <Truncated>{skill.name}</Truncated>
        </Text>
      </Box>
      <Text size="xs" color="subtle">
        Updated <FormattedRelativeTime date={skill.updatedAt} withTooltip={true} shouldUppercaseFirst={false} />
      </Text>
    </PlainButton>
  );
}

function SkillsContent() {
  const { skills, isLoading, isError, maxSkills } = useSkills();
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;

  if (isLoading) {
    return <PageLoader label="Loading skills" />;
  }

  if (isError) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flexGrow="1">
        <EmptyState>
          <EmptyStateTitle icon="error">Unable to load skills</EmptyStateTitle>
          <EmptyStateDescription>Something went wrong. Please try again later.</EmptyStateDescription>
        </EmptyState>
      </Box>
    );
  }

  if (skills.length === 0) {
    const limitText = Number.isFinite(maxSkills)
      ? `You can create up to ${String(maxSkills)} skills on your plan.`
      : '';

    return (
      <Box display="flex" flexDirection="column" alignItems="center" p="300">
        <EmptyState>
          <EmptyStateTitle icon="star">No custom skills yet</EmptyStateTitle>
          <EmptyStateDescription>
            Skills teach the agent specialized knowledge. Create one by asking the agent to save instructions to a skill
            file, or create one manually. {limitText}
          </EmptyStateDescription>
          <EmptyStateActions>
            <Button
              variant="primary"
              size="sm"
              icon="add"
              href={generatePath('/workspaces/:workspaceId/agent/skills/new', { workspaceId })}
              isDisabled={skills.length >= maxSkills}
            >
              New skill
            </Button>
          </EmptyStateActions>
        </EmptyState>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="200" w="full" p="300">
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Text size="s" weight="semibold" color="subtle">
          Your skills ({skills.length}
          {Number.isFinite(maxSkills) ? ` / ${String(maxSkills)}` : ''})
        </Text>
        <Button
          variant="primary"
          size="sm"
          icon="add"
          href={generatePath('/workspaces/:workspaceId/agent/skills/new', { workspaceId })}
          isDisabled={skills.length >= maxSkills}
        >
          New skill
        </Button>
      </Box>
      <Stack gap="100">
        {skills.map((skill) => (
          <SkillCard key={skill.path} skill={skill} workspaceId={workspaceId} />
        ))}
      </Stack>
    </Box>
  );
}

export function SkillsPage() {
  return (
    <AgentLayout title="Skills">
      <PageTitle title="Skills" />
      <Box overflow="auto" flexGrow="1">
        <SkillsContent />
      </Box>
    </AgentLayout>
  );
}
